import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { usePortals } from "../hooks/usePortals";
import type { Portal } from "@/types";
import AgentsImportPanel from "./panels/AgentsImportPanel";
import CmsPagesImportPanel from "./panels/CmsPagesImportPanel";
import FilesImportPanel from "./panels/FilesImportPanel";
import CopyHistoryPanel from "./panels/CopyHistoryPanel";
import CopySummaryModal from "./components/CopySummaryModal";
import type { CopyOperationResult } from "./types";

interface ImportContentTabProps {
  portal: Portal;
}

const ImportContentTab: React.FC<ImportContentTabProps> = ({ portal }) => {
  const [subTab, setSubTab] = useState(0);
  const [sourcePortalId, setSourcePortalId] = useState<string>("");
  const [summary, setSummary] = useState<CopyOperationResult | null>(null);

  const { portals, isLoading } = usePortals();

  const sourceOptions = useMemo(
    () => portals.filter((p) => p.id !== portal.id),
    [portals, portal.id],
  );

  const showSummary = (result: CopyOperationResult) => setSummary(result);

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="h6" fontWeight={700}>
          Import content from another portal
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Copy agents, CMS pages, and files from an existing portal. Items that
          were already copied into this portal are disabled. This action does
          not copy knowledge bases, roles, or relationships between objects —
          those must be set up manually.
        </Typography>
      </Box>

      <Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          Source portal
        </Typography>
        <Select
          value={sourcePortalId}
          onChange={(e) => setSourcePortalId(e.target.value as string)}
          displayEmpty
          size="small"
          sx={{ minWidth: 320 }}
          disabled={isLoading}
        >
          <MenuItem value="">
            <em>Select a source portal</em>
          </MenuItem>
          {sourceOptions.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography fontWeight={600}>{p.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {p.slug}
                </Typography>
              </Stack>
            </MenuItem>
          ))}
        </Select>
        {sourceOptions.length === 0 && !isLoading && (
          <Alert severity="info" sx={{ mt: 1 }}>
            No other portals available.
          </Alert>
        )}
      </Box>

      <Box
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Tabs value={subTab} onChange={(_, v) => setSubTab(v)}>
          <Tab label="Agents" />
          <Tab label="CMS Pages" />
          <Tab label="Files" />
          <Tab label="Copy History" />
        </Tabs>
      </Box>

      <Box>
        {subTab === 0 && (
          <AgentsImportPanel
            destPortalId={portal.id}
            sourcePortalId={sourcePortalId}
            onResult={showSummary}
          />
        )}
        {subTab === 1 && (
          <CmsPagesImportPanel
            destPortalId={portal.id}
            sourcePortalId={sourcePortalId}
            onResult={showSummary}
          />
        )}
        {subTab === 2 && (
          <FilesImportPanel
            destPortalId={portal.id}
            sourcePortalId={sourcePortalId}
            onResult={showSummary}
          />
        )}
        {subTab === 3 && <CopyHistoryPanel destPortalId={portal.id} />}
      </Box>

      <CopySummaryModal
        open={!!summary}
        result={summary}
        onClose={() => setSummary(null)}
      />
    </Stack>
  );
};

// Re-export Chip so the panels can use it without circular deps
export { Chip };
export default ImportContentTab;
